import { Link } from "react-router-dom";
import { useI18n } from "@/contexts/I18nContext";
import { usePatientAuth } from "@/contexts/PatientAuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import LanguageToggle from "@/components/LanguageToggle";
import { HeartPulse, Search, Clock, ShieldCheck, ArrowRight, MapPin, Stethoscope } from "lucide-react";

export default function PatientPortal() {
  const { t } = useI18n();
  const { user } = usePatientAuth();

  return (
    <div className="min-h-screen bg-white" data-testid="patient-portal-page">
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between border-b border-slate-100">
        <Link to="/patient" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center">
            <HeartPulse className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading text-base font-bold tracking-tight text-slate-900">UpchaarQ Care</span>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageToggle />
          <Link to="/for-doctors">
            <Button size="sm" className="bg-slate-500 hover:bg-slate-600 text-white text-xs" data-testid="goto-doctor-login">For Doctors</Button>
          </Link>
          {user ? (
            <Link to="/patient/home"><Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="goto-my-home">My Bookings</Button></Link>
          ) : (
            <Link to="/patient/login"><Button size="sm" className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="goto-patient-login">Login</Button></Link>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 sm:px-8 py-12 sm:py-20 max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-sky-600 font-semibold">For patients</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05] mt-3">
            Book doctors.{" "}
            <span className="brand-text">Skip the queue.</span>
          </h1>
          <p className="mt-5 text-base text-slate-600 max-w-md">
            See live token numbers, estimated wait time, and pay directly via UPI. No commission, no waiting room.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link to="/patient/search">
              <Button className="bg-sky-500 hover:bg-sky-600 text-white h-12 px-6 gap-2" data-testid="patient-find-doctor-btn">
                <Search className="w-4 h-4" /> {t("find_doctor")}
              </Button>
            </Link>
            <Link to={user ? "/patient/home" : "/patient/login"}>
              <Button variant="outline" className="h-12 px-6 gap-2" data-testid="patient-my-bookings-btn">
                <Clock className="w-4 h-4" /> My Bookings
              </Button>
            </Link>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-emerald-100 opacity-60" />
          <div className="absolute -bottom-12 -left-12 w-56 h-56 rounded-full bg-sky-100 opacity-60" />
          <Card className="relative p-6 medical-card">
            <Stat icon={Stethoscope} title="Trusted doctors" value="Verified by hospitals" />
            <Stat icon={MapPin} title="Live queue" value="Real-time token tracking" />
            <Stat icon={ShieldCheck} title="0% commission" value="Pay direct to clinic via UPI" />
          </Card>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-slate-50 border-t border-slate-100 py-12 sm:py-16 px-4 sm:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">How it works</h2>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            <Step n="1" title="Search" body="Find doctors by name, city or specialization." />
            <Step n="2" title="Book" body="Pick a doctor, see live queue and book your token." />
            <Step n="3" title="Pay via UPI" body="Pay direct to the clinic. Walk in when it's your turn." />
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, title, value }) {
  return (
    <div className="flex items-center gap-3 py-3 border-b last:border-b-0 border-slate-100">
      <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center"><Icon className="w-5 h-5 text-sky-600" /></div>
      <div>
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500">{value}</div>
      </div>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <Card className="medical-card p-5">
      <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-heading font-bold text-sm">{n}</div>
      <div className="mt-3 font-heading text-lg font-semibold text-slate-900">{title}</div>
      <p className="mt-1 text-sm text-slate-500">{body}</p>
      <ArrowRight className="w-4 h-4 text-slate-300 mt-3" />
    </Card>
  );
}
